import { useState } from 'react';
import { CheckCircle2, Play, XCircle } from 'lucide-react';
import { Button } from '../../button/button';
import { Input } from '../../input';
import { SecretInput } from '../../secret-input';
import { Textarea } from '../../textarea';
import { cn } from '../../../../lib/utils';
import type {
  ConnectionDraft,
  ConnectionFormat,
  ConnectionTestResult,
} from '../../../../lib/external-agents/types';
import type {
  DraftField,
  DraftProblems,
} from '../../../../lib/external-agents/validate';
import { INSET_CLASS } from '../constants';
import { ChoiceGroup, FieldLabel, FieldMessage } from '../form-parts';
import { Guide } from '../guides/connection-guides';
import { StepTitle } from '../wizard-frame';

const DEFAULT_TEMPLATES = {
  stateless: '{ "messages": "{{messages}}" }',
  session: '{ "message": "{{message}}", "session_id": "{{session_id}}" }',
};

const FORMATS: { value: ConnectionFormat; title: string; detail: string }[] = [
  {
    value: 'json',
    title: 'Plain JSON',
    detail:
      'Any HTTPS API that takes and returns JSON. You tell us where the reply is.',
  },
  {
    value: 'openai',
    title: 'OpenAI-compatible',
    detail: 'Speaks /chat/completions: vLLM, LiteLLM, Ollama, most gateways.',
  },
  {
    value: 'anthropic',
    title: 'Anthropic-compatible',
    detail: "Speaks Anthropic's Messages format at /v1/messages.",
  },
];

export function FormatStep({
  value,
  onChange,
}: {
  value: ConnectionFormat;
  onChange: (format: ConnectionFormat) => void;
}) {
  return (
    <>
      <StepTitle>What does your endpoint speak?</StepTitle>
      <div
        role="radiogroup"
        aria-label="Format"
        className="grid gap-2 md:grid-cols-3"
      >
        {FORMATS.map((format) => (
          <label
            key={format.value}
            className={cn(
              'flex cursor-pointer gap-3 rounded-lg border bg-prompt-safecomwhite p-3',
              value === format.value
                ? 'ring-prompt-safecomoutrageous-orange/15 border-prompt-safecomoutrageous-orange ring-2'
                : 'border-[#CED4DA]',
            )}
          >
            <input
              type="radio"
              name="endpoint-format"
              value={format.value}
              checked={value === format.value}
              onChange={() => onChange(format.value)}
              className="mt-1 accent-prompt-safecomoutrageous-orange"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-prompt-safecomvulcan">
                {format.title}
              </span>
              <span className="text-xs text-prompt-safecomabbey">
                {format.detail}
              </span>
            </span>
          </label>
        ))}
      </div>
    </>
  );
}

/** Each field's input, in form order, so the wizard can focus the first problem. */
export const FIELD_IDS: Record<DraftField, string> = {
  endpointUrl: 'endpoint-url',
  authHeaderName: 'endpoint-header-name',
  secret: 'endpoint-secret',
  model: 'endpoint-model',
  sessionResponsePath: 'endpoint-session-path',
  requestTemplate: 'endpoint-template',
  replyPath: 'endpoint-reply-path',
};

/**
 * A field's problem shows once something was typed and the field was left,
 * or for every field once Continue was pressed.
 */
function useFieldFeedback(
  errors: DraftProblems,
  warnings: DraftProblems,
  showAllErrors: boolean,
) {
  const [touched, setTouched] = useState<Set<DraftField>>(new Set());
  const error = (field: DraftField) =>
    showAllErrors || touched.has(field) ? errors[field] : undefined;
  const messageId = (field: DraftField) => `${FIELD_IDS[field]}-message`;
  return {
    error,
    /** Ties an input to its message. */
    props: (field: DraftField) => ({
      id: FIELD_IDS[field],
      onBlur: (
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        if (event.target.value.trim()) {
          setTouched((fields) => new Set(fields).add(field));
        }
      },
      'aria-invalid': Boolean(error(field)),
      'aria-describedby':
        error(field) || warnings[field] ? messageId(field) : undefined,
    }),
    message: (field: DraftField) => (
      <FieldMessage
        id={messageId(field)}
        error={error(field)}
        warning={warnings[field]}
      />
    ),
  };
}

type FieldFeedback = ReturnType<typeof useFieldFeedback>;

interface EndpointStepProps {
  draft: ConnectionDraft;
  onChange: (draft: ConnectionDraft) => void;
  secret: string;
  onSecretChange: (secret: string) => void;
  /** Editing a saved agent: a blank secret keeps the saved one. */
  hasSavedSecret: boolean;
  errors: DraftProblems;
  warnings: DraftProblems;
  /** Continue was pressed with problems left. */
  showAllErrors: boolean;
}

export function EndpointStep({
  draft,
  onChange,
  secret,
  onSecretChange,
  hasSavedSecret,
  errors,
  warnings,
  showAllErrors,
}: EndpointStepProps) {
  const field = useFieldFeedback(errors, warnings, showAllErrors);
  const set = <K extends keyof ConnectionDraft>(
    key: K,
    value: ConnectionDraft[K],
  ) => onChange({ ...draft, [key]: value });
  const isJson = draft.format === 'json';
  const secretPlaceholder = hasSavedSecret
    ? 'Leave blank to keep the saved one'
    : isJson
      ? 'The token your endpoint checks'
      : 'Your API key';

  return (
    <>
      <StepTitle>Where is it, and how does it check who's calling?</StepTitle>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <FieldLabel
            htmlFor="endpoint-url"
            help={
              isJson
                ? 'Public HTTPS. It takes a JSON POST and replies with JSON within 60 seconds.'
                : draft.format === 'openai'
                  ? 'PromptSafe calls <base URL>/chat/completions.'
                  : 'PromptSafe calls <base URL>/v1/messages.'
            }
          >
            {isJson ? 'Endpoint URL' : 'Base URL'}
          </FieldLabel>
          <Input
            {...field.props('endpointUrl')}
            type="url"
            value={draft.endpointUrl}
            onChange={(event) => set('endpointUrl', event.target.value)}
            placeholder={
              isJson
                ? 'https://bot.example.com/chat'
                : draft.format === 'openai'
                  ? 'https://llm.example.com/v1'
                  : 'https://gateway.example.com'
            }
            className="bg-prompt-safecomwhite font-mono text-xs"
          />
          {field.message('endpointUrl')}
        </div>

        {isJson && (
          <div className="flex flex-col gap-1.5">
            <FieldLabel help="How the secret is sent with every request.">
              Authentication
            </FieldLabel>
            <ChoiceGroup
              label="Authentication"
              choices={[
                { value: 'bearer', label: 'Bearer token' },
                { value: 'header', label: 'Named header' },
              ]}
              value={draft.authType}
              onChange={(value) => set('authType', value)}
            />
            {draft.authType === 'header' && (
              <>
                <Input
                  {...field.props('authHeaderName')}
                  aria-label="Header name"
                  value={draft.authHeaderName}
                  onChange={(event) =>
                    set('authHeaderName', event.target.value)
                  }
                  className="bg-prompt-safecomwhite font-mono text-xs"
                />
                {field.message('authHeaderName')}
              </>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <FieldLabel
            htmlFor="endpoint-secret"
            help="Stored encrypted and sent only to this endpoint. Never shown again after saving."
          >
            {isJson ? 'Secret' : 'API key'}
          </FieldLabel>
          <SecretInput
            {...field.props('secret')}
            value={secret}
            onChange={(event) => onSecretChange(event.target.value)}
            placeholder={secretPlaceholder}
            className="bg-prompt-safecomwhite"
          />
          {field.message('secret')}
        </div>

        {!isJson && (
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="endpoint-model"
              help="Sent as the model field, exactly as your server expects it."
            >
              Model
            </FieldLabel>
            <Input
              {...field.props('model')}
              value={draft.model}
              onChange={(event) => set('model', event.target.value)}
              placeholder={
                draft.format === 'openai' ? 'llama-3.3-70b' : 'claude-sonnet-5'
              }
              className="bg-prompt-safecomwhite font-mono text-xs"
            />
            {field.message('model')}
          </div>
        )}

        {isJson && (
          <JsonConversationFields draft={draft} set={set} field={field} />
        )}
      </div>

      <details className="bg-prompt-safecomwhite/60 rounded-lg border border-[#00000014] p-4">
        <summary className="cursor-pointer text-sm font-medium text-prompt-safecomvulcan">
          How your endpoint should look
        </summary>
        <div className="mt-4">
          <Guide id={draft.format} />
        </div>
      </details>
    </>
  );
}

function JsonConversationFields({
  draft,
  set,
  field,
}: {
  draft: ConnectionDraft;
  set: <K extends keyof ConnectionDraft>(
    key: K,
    value: ConnectionDraft[K],
  ) => void;
  field: FieldFeedback;
}) {
  // Open when there's something custom in it, or a problem to see.
  const [advancedOpen, setAdvancedOpen] = useState(
    Boolean(draft.requestTemplate.trim() || draft.replyPath.trim()),
  );
  const advancedProblem = Boolean(
    field.error('requestTemplate') || field.error('replyPath'),
  );
  return (
    <>
      <div className="flex flex-col gap-1.5 md:col-span-2">
        <FieldLabel help="Full history: every request carries the whole conversation. Session: only the latest message, plus a session ID your endpoint uses to remember the rest.">
          Conversation
        </FieldLabel>
        <ChoiceGroup
          label="Conversation"
          choices={[
            { value: 'session', label: 'Session ID' },
            { value: 'stateless', label: 'Full history each time' },
          ]}
          value={draft.conversationMode}
          onChange={(value) => set('conversationMode', value)}
        />
      </div>

      {draft.conversationMode === 'session' && (
        <>
          <div className="flex flex-col gap-1.5">
            <FieldLabel help="Each simulation is one conversation, so your bot needs an ID to keep simulations apart. Your endpoint: it creates the ID in its first reply, and PromptSafe sends it back after that. PromptSafe: it creates one ID per simulation and sends it from the first message, for bots that accept any ID, like one a website assigns to each visitor.">
              Who issues the session ID
            </FieldLabel>
            <ChoiceGroup
              label="Who issues the session ID"
              choices={[
                { value: 'response', label: 'Your endpoint' },
                { value: 'generated', label: 'PromptSafe' },
              ]}
              value={draft.sessionIdSource}
              onChange={(value) => set('sessionIdSource', value)}
            />
          </div>
          {draft.sessionIdSource === 'response' && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel
                htmlFor="endpoint-session-path"
                help="Where the session ID is in your endpoint's first reply, like conversation_id or data.session.id."
              >
                Field with the session ID
              </FieldLabel>
              <Input
                {...field.props('sessionResponsePath')}
                value={draft.sessionResponsePath}
                onChange={(event) =>
                  set('sessionResponsePath', event.target.value)
                }
                className="bg-prompt-safecomwhite font-mono text-xs"
              />
              {field.message('sessionResponsePath')}
            </div>
          )}
        </>
      )}

      <details
        open={advancedOpen || advancedProblem}
        onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
        className="border-t border-[#00000014] pt-4 md:col-span-2"
      >
        <summary className="cursor-pointer text-sm text-prompt-safecomabbey">
          Request shape and reply location
        </summary>
        <div className="mt-4 grid gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="endpoint-template"
              help='The JSON we send. Placeholders are replaced as whole values: "{{messages}}" (the conversation as role/content pairs), "{{message}}" (the latest message), "{{session_id}}", "{{model}}".'
            >
              Request template
            </FieldLabel>
            <Textarea
              {...field.props('requestTemplate')}
              value={draft.requestTemplate}
              onChange={(event) => set('requestTemplate', event.target.value)}
              placeholder={DEFAULT_TEMPLATES[draft.conversationMode]}
              className="min-h-[88px] bg-prompt-safecomwhite font-mono text-xs"
            />
            {field.message('requestTemplate')}
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="endpoint-reply-path"
              help="Where the reply text is in your JSON, like answer.text or data.0.text."
            >
              Reply path
            </FieldLabel>
            <Input
              {...field.props('replyPath')}
              value={draft.replyPath}
              onChange={(event) => set('replyPath', event.target.value)}
              placeholder="response"
              className="bg-prompt-safecomwhite font-mono text-xs"
            />
            {field.message('replyPath')}
          </div>
        </div>
      </details>
    </>
  );
}

/** A test that couldn't run, like settings the server refused. */
export interface TestFailure {
  message: string;
  /** The settings themselves are the problem. */
  fixable: boolean;
}

interface TestStepProps {
  result: ConnectionTestResult | null;
  failure: TestFailure | null;
  isRunning: boolean;
  onRun: () => void;
  onEditSettings: () => void;
}

/** Required: saving is only possible with the receipt of a passed test. */
export function TestStep({
  result,
  failure,
  isRunning,
  onRun,
  onEditSettings,
}: TestStepProps) {
  const editHint = (
    <p className="text-xs text-prompt-safecomabbey">
      <button
        type="button"
        onClick={onEditSettings}
        className="font-medium text-prompt-safecomoutrageous-orange underline-offset-2 hover:underline"
      >
        Change the settings
      </button>
      , then test again.
    </p>
  );

  return (
    <>
      <div className="flex flex-col gap-1">
        <StepTitle>Test the connection</StepTitle>
        <p className="text-sm text-prompt-safecomabbey">
          Sends a test message the same way a simulation will. You can save once
          it passes.
        </p>
      </div>
      <div>
        <Button variant="outline" onClick={onRun} disabled={isRunning}>
          <Play className="h-4 w-4" />
          {isRunning ? 'Testing…' : result ? 'Test again' : 'Run test'}
        </Button>
      </div>

      {failure && (
        <div
          role="alert"
          className={cn('flex flex-col gap-3 p-4 text-sm', INSET_CLASS)}
        >
          <p className="flex items-start gap-2 text-red-800">
            <XCircle className="mt-0.5 h-4 w-4 flex-none text-red-700" />
            {failure.message}
          </p>
          {failure.fixable && editHint}
        </div>
      )}

      {result && (
        <div className={cn('flex flex-col gap-3 p-4 text-sm', INSET_CLASS)}>
          <ul className="flex flex-col gap-1.5">
            {result.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-2">
                {step.ok ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-green-700" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 flex-none text-red-700" />
                )}
                <span className="flex flex-col">
                  <span className="text-prompt-safecomvulcan">
                    {step.label}
                  </span>
                  {step.detail && (
                    <span
                      className={cn(
                        'text-xs',
                        step.ok ? 'text-prompt-safecomabbey' : 'text-red-800',
                      )}
                    >
                      {step.detail}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {result.ok && (
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 border-t border-dashed border-[#00000014] pt-2 text-prompt-safecomabbey">
              <dt>Sent</dt>
              <dd className="text-prompt-safecomvulcan">"{result.sent}"</dd>
              <dt>Reply</dt>
              <dd className="whitespace-pre-wrap text-prompt-safecomvulcan">
                "{result.reply}"
              </dd>
            </dl>
          )}
          {!result.ok && editHint}
        </div>
      )}
    </>
  );
}

interface SaveStepProps {
  name: string;
  onNameChange: (name: string) => void;
  greeting: string;
  onGreetingChange: (greeting: string) => void;
}

export function SaveStep({
  name,
  onNameChange,
  greeting,
  onGreetingChange,
}: SaveStepProps) {
  return (
    <>
      <StepTitle>Name it</StepTitle>
      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <FieldLabel htmlFor="endpoint-name">Name</FieldLabel>
          <Input
            id="endpoint-name"
            value={name}
            maxLength={100}
            onChange={(event) => onNameChange(event.target.value)}
            placeholder="Acme Support Bot"
            className="bg-prompt-safecomwhite"
          />
        </div>
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <FieldLabel
            htmlFor="endpoint-greeting"
            help="The first line of every simulation, like the first bubble in your chat widget. Leave it blank if your product doesn't show one."
          >
            Opening message
          </FieldLabel>
          <Input
            id="endpoint-greeting"
            value={greeting}
            maxLength={500}
            onChange={(event) => onGreetingChange(event.target.value)}
            placeholder="Optional: your agent's opening message"
            className="bg-prompt-safecomwhite"
          />
          <p className="text-xs text-prompt-safecomabbey">
            Optional. Your agent never sends it, but the persona replies as if
            it did. Leave it blank and the persona starts the conversation.
          </p>
        </div>
      </div>
    </>
  );
}
